import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import App from "./App.tsx";
import "./index.css";
import "./lib/i18n/config"; // Initialize i18n before app renders
import { configureRevenueCat } from "./lib/revenueCat";

// CRITICAL: must run BEFORE any module imports the Supabase client.
// Supabase's `detectSessionInUrl` auto-parses the recovery hash and then
// clears it via history.replaceState on client init — by the time React
// mounts, the `type=recovery` marker is gone from the URL.
//
// Supabase's recovery email redirects to the Site URL (root) regardless of
// the `redirectTo` we pass, so users arrive at "/" with the recovery hash
// and no code on the root route is looking for it. Here we detect the
// recovery flow at module load time, redirect to /auth preserving the hash
// (and stash a flag in sessionStorage as a belt-and-suspenders signal),
// and let Auth.tsx take over from there.
(() => {
  if (typeof window === "undefined") return;
  try {
    const { pathname, hash, search } = window.location;
    const hashHasRecovery = hash.includes("type=recovery");
    const searchParams = new URLSearchParams(search);
    const queryHasRecovery = searchParams.get("type") === "recovery";
    const onAuthRoute = pathname === "/auth";

    if ((hashHasRecovery || queryHasRecovery) && !onAuthRoute) {
      // Persist a sentinel so Auth.tsx can recognize the flow even if
      // the Supabase client clears the hash before we finish rendering.
      try { sessionStorage.setItem("pendingPasswordRecovery", "1"); } catch {}
      const newUrl = "/auth" + (search || "") + (hash || "");
      window.history.replaceState({}, "", newUrl);
    } else if (onAuthRoute && (hashHasRecovery || queryHasRecovery)) {
      // Already on /auth — still set the sentinel so Auth.tsx can detect it
      // even after Supabase clears the hash.
      try { sessionStorage.setItem("pendingPasswordRecovery", "1"); } catch {}
    }
  } catch {
    // Fall through — the in-Auth.tsx detection is still a backstop.
  }
})();

// Initialize Sentry error monitoring (only when DSN is configured)
const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: import.meta.env.MODE,
    integrations: [
      Sentry.browserTracingIntegration(),
    ],
    tracesSampleRate: 0.1, // 10% of transactions for performance monitoring
    replaysOnErrorSampleRate: 1.0, // Capture 100% of sessions with errors
    beforeSend(event) {
      // Don't send events in development
      if (import.meta.env.DEV) return null;
      return event;
    },
  });
}

// Prevent service worker from running in iframes (e.g. preview environments)
const isInIframe = (() => {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true;
  }
})();

const isPreviewHost =
  window.location.hostname.includes("id-preview--") ||
  window.location.hostname.includes("staging.");

if (isPreviewHost || isInIframe) {
  navigator.serviceWorker?.getRegistrations().then((registrations) => {
    registrations.forEach((r) => r.unregister());
  });
}

// Auto-reload when a new service worker activates (production only)
if (!isPreviewHost && !isInIframe && "serviceWorker" in navigator) {
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    window.location.reload();
  });
}

// Belt-and-suspenders: catch chunk-load errors from non-lazy() dynamic imports
// (router-level code splits, third-party libs that import on demand, etc.).
// `lazyWithRetry` handles the React.lazy() path; this catches everything else.
// Reloads at most once per 30s window per session to avoid loops.
if (typeof window !== "undefined") {
  const CHUNK_RELOAD_KEY = "__chunkReloadAt";
  const COOLDOWN_MS = 30_000;
  const looksLikeChunkLoadError = (reason: unknown) => {
    const msg = String(
      (reason as { message?: string })?.message ?? reason ?? "",
    ).toLowerCase();
    return (
      msg.includes("failed to fetch dynamically imported module") ||
      msg.includes("importing a module script failed") ||
      msg.includes("error loading dynamically imported module")
    );
  };
  const maybeReload = () => {
    try {
      const last = parseInt(sessionStorage.getItem(CHUNK_RELOAD_KEY) ?? "0", 10);
      if (Date.now() - last < COOLDOWN_MS) return;
      sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
    } catch {
      // sessionStorage unavailable; reload anyway.
    }
    window.location.reload();
  };
  window.addEventListener("unhandledrejection", (event) => {
    if (looksLikeChunkLoadError(event.reason)) maybeReload();
  });
  window.addEventListener("error", (event) => {
    if (looksLikeChunkLoadError(event.error ?? event.message)) maybeReload();
  });
}

// Kick off RevenueCat SDK configuration in the background. No-op on
// non-iOS platforms, and useAuth.tsx's auth listener calls
// identifyRevenueCatUser() once a Supabase session lands.
void configureRevenueCat();

createRoot(document.getElementById("root")!).render(<App />);
